import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  Heart, 
  Brain, 
  DollarSign, 
  Users, 
  GraduationCap, 
  Briefcase,
  Filter,
  Calendar,
  ArrowRight
} from 'lucide-react';
import { useGameStore } from '../store/gameStore';

const Timeline: React.FC = () => {
  const { timeline, currentAge } = useGameStore();
  const [filter, setFilter] = useState<'all' | 'decision' | 'milestone' | 'consequence'>('all');

  const filteredTimeline = filter === 'all' 
    ? timeline 
    : timeline.filter(event => event.type === filter);

  const getEventIcon = (type: string) => {
    const icons = {
      decision: ArrowRight,
      milestone: Calendar,
      consequence: Clock,
    };
    return icons[type as keyof typeof icons] || Clock;
  };

  const getEventColor = (type: string) => {
    const colors = {
      decision: 'from-blue-500 to-cyan-500',
      milestone: 'from-green-500 to-emerald-500',
      consequence: 'from-orange-500 to-yellow-500',
    };
    return colors[type as keyof typeof colors] || 'from-slate-500 to-slate-600';
  };

  const getStatIcon = (stat: string) => {
    const icons = {
      health: Heart,
      happiness: Brain,
      wealth: DollarSign,
      relationships: Users,
      education: GraduationCap,
      career: Briefcase,
    };
    return icons[stat as keyof typeof icons] || Heart;
  };

  const getStatChangeColor = (value: number) => {
    if (value > 0) return 'text-success-600';
    if (value < 0) return 'text-danger-600';
    return 'text-slate-600';
  };

  const getStatChangeIcon = (value: number) => {
    if (value > 0) return '↗️';
    if (value < 0) return '↘️';
    return '→';
  };

  const filters = [
    { id: 'all', name: 'All Events', icon: Filter },
    { id: 'decision', name: 'Decisions', icon: ArrowRight },
    { id: 'milestone', name: 'Milestones', icon: Calendar },
    { id: 'consequence', name: 'Consequences', icon: Clock },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <h1 className="text-4xl font-bold text-slate-800 mb-4">Your Life Timeline</h1>
        <p className="text-xl text-slate-600 max-w-2xl mx-auto">
          Track your journey through life and see how your decisions have shaped your path
        </p>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="flex flex-wrap gap-2 justify-center"
      >
        {filters.map((filterOption) => {
          const Icon = filterOption.icon;
          return (
            <button
              key={filterOption.id}
              onClick={() => setFilter(filterOption.id as any)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${
                filter === filterOption.id
                  ? 'bg-primary-100 text-primary-700 shadow-soft'
                  : 'bg-white/80 text-slate-600 hover:bg-primary-50 hover:text-primary-600'
              }`}
            >
              <Icon size={18} />
              <span className="font-medium">{filterOption.name}</span>
            </button>
          );
        })}
      </motion.div>

      {/* Timeline */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="relative"
      >
        {filteredTimeline.length > 0 ? (
          <div className="space-y-6">
            {filteredTimeline.map((event, index) => {
              const Icon = getEventIcon(event.type);
              const gradientColor = getEventColor(event.type);
              
              return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative flex items-start space-x-6"
                >
                  {/* Timeline Line */}
                  {index < filteredTimeline.length - 1 && (
                    <div className="absolute left-6 top-12 w-0.5 h-16 bg-slate-200" />
                  )}
                  
                  {/* Event Icon */}
                  <div className={`w-12 h-12 bg-gradient-to-br ${gradientColor} rounded-full flex items-center justify-center shadow-medium z-10`}>
                    <Icon size={24} className="text-white" />
                  </div>

                  {/* Event Content */}
                  <div className="flex-1 bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-soft border border-white/20">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-slate-800 mb-1">
                          {event.title}
                        </h3>
                        <p className="text-slate-600 mb-2">{event.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-slate-500">
                          <div className="flex items-center space-x-1">
                            <Calendar size={16} />
                            <span>Age {event.age}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock size={16} />
                            <span className="capitalize">{event.type}</span>
                          </div>
                        </div>
                      </div>
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: event.color }}
                      />
                    </div>

                    {/* Impact Stats */}
                    {event.impact && Object.keys(event.impact).length > 0 && (
                      <div className="bg-slate-50 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">Impact</h4>
                        <div className="flex flex-wrap gap-3">
                          {Object.entries(event.impact).map(([stat, change]) => {
                            const StatIcon = getStatIcon(stat);
                            return (
                              <div
                                key={stat}
                                className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
                                  change && change > 0
                                    ? 'bg-success-100 text-success-700'
                                    : change && change < 0
                                    ? 'bg-danger-100 text-danger-700'
                                    : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                <StatIcon size={16} />
                                <span className="text-sm font-medium capitalize">{stat}</span>
                                <span className="text-sm font-bold">
                                  {getStatChangeIcon(change || 0)} {change && change > 0 ? '+' : ''}{change}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="text-center py-16 bg-white/50 rounded-2xl border border-slate-200"
          >
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock size={40} className="text-slate-400" />
            </div>
            <h3 className="text-2xl font-semibold text-slate-600 mb-4">No Timeline Events</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              {filter === 'all' 
                ? "Your life timeline is empty. Start making decisions to see your journey unfold."
                : `No ${filter} events found. Try a different filter or make some decisions.`
              }
            </p>
          </motion.div>
        )}
      </motion.div>

      {/* Current Age Indicator */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-6 text-white text-center"
      >
        <h3 className="text-2xl font-bold mb-2">Current Age: {currentAge}</h3>
        <p className="text-primary-100">
          Continue making decisions to shape your future and add more events to your timeline
        </p>
      </motion.div>
    </div>
  );
};

export default Timeline;
